import { useState } from 'react';
import { History, RotateCcw, Bookmark, Rocket, Trash2, Sparkles, Undo2 } from 'lucide-react';
import Button from '@/components/ui/Button';
import ConfirmDialog from '@/components/sandbox/ConfirmDialog';
import { useAuthStore } from '@/stores/auth-store';
import { useUIStore } from '@/stores/ui-store';
import {
  useRestorePoints,
  useCreateRestorePoint,
  useRestoreToPoint,
  type RestorePoint,
} from '@/hooks/useContentRevisions';

/**
 * Design History — what changed on this site, and how to put it back.
 *
 * The Design Sandbox answers "what could the site look like". This answers
 * "what did it look like, and can I have that back". They are deliberately
 * separate: sandbox drafts are candidates a person named and might publish,
 * while restore points are automatic and would swamp that gallery.
 *
 * A restore point is the whole design surface in one row — theme, logos,
 * homepage sections, CMS pages — so recovering from a bad publish is one
 * action rather than a hunt through per-field history.
 */

function kindLabel(kind: RestorePoint['kind']): { text: string; className: string; Icon: typeof Rocket } {
  switch (kind) {
    case 'manual':
      return { text: 'Saved by you', className: 'bg-sage-100 text-sage-700', Icon: Bookmark };
    case 'pre_publish':
      return { text: 'Before a publish', className: 'bg-blue-100 text-blue-700', Icon: Rocket };
    case 'pre_restore':
      return { text: 'Before a restore', className: 'bg-amber-100 text-amber-700', Icon: Undo2 };
    default:
      return { text: 'Automatic', className: 'bg-gray-100 text-gray-600', Icon: Sparkles };
  }
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function DesignHistory() {
  const user = useAuthStore((s) => s.user);
  const addToast = useUIStore((s) => s.addToast);
  const canPublish = (user?.capabilities ?? []).includes('design:publish');

  const { data: points, isLoading } = useRestorePoints();
  const createPoint = useCreateRestorePoint();
  const restoreToPoint = useRestoreToPoint();

  const [saveOpen, setSaveOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [pending, setPending] = useState<RestorePoint | null>(null);

  async function handleSave() {
    const trimmed = label.trim();
    if (!trimmed) return;
    try {
      await createPoint.mutateAsync(trimmed);
      addToast('Restore point saved', 'success');
      setSaveOpen(false);
      setLabel('');
    } catch {
      addToast('Could not save a restore point', 'error');
    }
  }

  async function handleRestore() {
    if (!pending) return;
    try {
      await restoreToPoint.mutateAsync(pending.id);
      addToast('Site restored — an undo point was saved first', 'success');
      setPending(null);
    } catch {
      addToast('Could not restore', 'error');
    }
  }

  return (
    <div className="max-w-[1000px] mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <History className="h-7 w-7 text-gray-400" aria-hidden="true" />
            Design History
          </h1>
          <p className="text-gray-600 mt-1">
            Every publish saves a restore point automatically. Restoring one puts the theme, logos,
            homepage sections and pages back as they were.
          </p>
        </div>
        <Button variant="secondary" onClick={() => setSaveOpen(true)}>
          <Bookmark className="h-4 w-4 mr-2" aria-hidden="true" />
          Save a restore point
        </Button>
      </div>

      {!canPublish && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          You can browse history here, but restoring changes the live site and needs the
          publish permission. Ask a site owner to run a restore for you.
        </div>
      )}

      {isLoading && <div className="text-gray-500 py-8">Loading history…</div>}

      {!isLoading && (points?.length ?? 0) === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 px-6 py-12 text-center">
          <History className="h-8 w-8 mx-auto text-gray-300 mb-3" aria-hidden="true" />
          <p className="text-gray-600 font-medium">No restore points yet</p>
          <p className="text-gray-500 text-sm mt-1">
            One is saved automatically the next time a design is published — or save one now to
            mark the current design as known-good.
          </p>
        </div>
      )}

      {!isLoading && (points?.length ?? 0) > 0 && (
        <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white">
          {points!.map((point) => {
            const { text, className, Icon } = kindLabel(point.kind);
            return (
              <li key={point.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${className}`}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                  {text}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-gray-900">{point.label}</div>
                  <div className="text-xs text-gray-500">{formatWhen(point.created_at)}</div>
                </div>
                <Button
                  variant="secondary"
                  onClick={() => setPending(point)}
                  disabled={!canPublish}
                  title={canPublish ? undefined : 'Restoring needs the publish permission'}
                >
                  <RotateCcw className="h-4 w-4 mr-2" aria-hidden="true" />
                  Restore
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      {saveOpen && (
        <ConfirmDialog
          title="Save a restore point"
          confirmLabel="Save"
          busy={createPoint.isPending}
          confirmDisabled={!label.trim()}
          onConfirm={handleSave}
          onCancel={() => {
            setSaveOpen(false);
            setLabel('');
          }}
        >
          <p className="mb-3 text-sm text-gray-600">
            Captures the current theme, logos, homepage sections and pages so you can come back to
            them later. Nothing on the live site changes.
          </p>
          <input
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-sage-500 focus:outline-none focus:ring-1 focus:ring-sage-500"
            placeholder="e.g. Before the spring refresh"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={200}
            autoFocus
          />
        </ConfirmDialog>
      )}

      {pending && (
        <ConfirmDialog
          title="Restore your live site?"
          confirmLabel="Restore"
          danger
          busy={restoreToPoint.isPending}
          onConfirm={handleRestore}
          onCancel={() => setPending(null)}
        >
          <p className="mb-3">
            Every visitor will see the design as it was on{' '}
            <strong>{formatWhen(pending.created_at)}</strong>.
          </p>
          {/* The destructive part, said plainly rather than discovered afterwards.
              Restore replaces the whole surface, so anything created since the
              point did not exist then and goes away. */}
          <div className="mb-3 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <Trash2 className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
            <span>
              Pages and homepage sections created since then will be removed, because they did not
              exist at that point.
            </span>
          </div>
          <p className="text-sm text-gray-600">
            An undo point is saved first, so you can reverse this.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}
